import {Injectable} from '@angular/core';
import {config} from "../../config";

@Injectable({
  providedIn: 'root'
})
export class DebugService {

  #actions = new Map<string, DebugAction<DebugParams>>();
  #winDbg: Record<string, unknown> = {};

  constructor() {

    if (!config.isLocal && config.isDev) {
      // eslint-disable-next-line
      (window as any).debug = this.#winDbg;
    } else {
      let enabled = false;
      Object.defineProperty(window, 'debug', {
        enumerable: false,
        configurable: false,
        get() {
          if (!enabled) {
            return;
          }
          return this.#winDbg;
        },
        set(value: unknown) {
          if (typeof value !== 'boolean') {
            return;
          }
          enabled = value;
        }
      })
    }
  }


  #setToWinDbg(action: DebugAction<DebugParams>) {
    let params: (readonly [string, DebugParamTypeName])[] = [];
    if (Array.isArray(action.params)) {
      if (action.params.length) {
        if (Array.isArray(action.params[0])) {
          params = action.params as (readonly [string, DebugParamTypeName])[];
        } else {
          params = action.params.map((type, i) => [`arg${i}`, type]) as (readonly [string, DebugParamTypeName])[];
        }
      }
    } else if (typeof action.params === 'object') {
      params = Object.entries(action.params) as (readonly [string, DebugParamTypeName])[];
    } else {
      if (!config.isProduction) {
        console.error(`Action ${action.name} has invalid params`, action);
      }
      return;
    }


    this.#winDbg[action.name] = (...args: unknown[]) => {

      if (params.length !== args.length) {
        throw new Error(`Action ${action.name} expected ${params.length} arguments, got ${args.length}`);
      }

      try {

        const argsArr = args.reduce((acc: [string, unknown][], arg, i) => {
          const [name, type] = params[i];
          if (typeof arg !== type) {
            throw new Error(`Action ${action.name} expected argument ${i} to be of type ${type}, got ${typeof arg}`);
          }
          acc.push([name, arg]);
          return acc;
        }, []);

        if (!Array.isArray(action.params)) {
          args = [Object.fromEntries(argsArr)];
        }
      } catch (e) {
        console.error(e);
        return;
      }

      return (action.action as unknown as (...args: unknown[]) => unknown)(...args);
    }
  }

  public tryRegister<T>(action: T extends DebugAction<infer P> ? DebugAction<P> : never) {
    if (!this.#actions.has(action.name)) {
      this.#actions.set(action.name, action);
      this.#setToWinDbg(action);
    }
  }

  public register(action: DebugAction<DebugParams>) {
    if (this.#actions.has(action.name)) {
      throw new Error(`Action with name ${action.name} already registered`);
    }
    this.#actions.set(action.name, action);
    this.#setToWinDbg(action);
  }
}

type DebugParamTypeName = 'string' | 'number' | 'boolean' | 'object' | 'array' | 'null' | 'undefined'

interface DebugParamType {
  'string': string,
  'number': number,
  'boolean': boolean,
  'object': Record<string, unknown>,
  'array': readonly unknown[],
  'null': null,
  'undefined': undefined
}

export type DebugParams = (readonly (readonly [string, DebugParamTypeName])[]) | (readonly DebugParamTypeName[]) | Readonly<Record<string, DebugParamTypeName>>;
type DebugParamsAsFuncArgs<TParams extends DebugParams> =
  TParams extends readonly (readonly [string, DebugParamTypeName])[] ? DebugParamType[TParams[number][1]][] :
  TParams extends readonly DebugParamTypeName[] ? DebugParamType[TParams[number]][] :
  TParams extends Readonly<Record<string, DebugParamTypeName>> ? [{ [K in keyof TParams]: TParams[K] }] : never;

export function AsDebugAction<T>(action: T): T extends DebugAction<infer P> ? DebugAction<P> : never
{
  return action as T extends DebugAction<infer P> ? DebugAction<P> : never;
}
export interface DebugAction<TParams extends DebugParams> {
  readonly name: string
  readonly params: TParams;
  readonly action: (...args: DebugParamsAsFuncArgs<TParams>) => (unknown | Promise<unknown>);
  readonly p: DebugParamsAsFuncArgs<TParams>;
}

